import React, { useEffect, useRef, useState, useCallback } from "react";
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import TileWMS from "ol/source/TileWMS.js";
import OSM from "ol/source/OSM";
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { defaults as defaultControls } from 'ol/control';
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Overlay from 'ol/Overlay';
import { WKT } from 'ol/format';
import { useGeographic } from 'ol/proj';
import useNominatim from '../hooks/useNominatim';
import { geocodeAddress } from '../services/nominatimClient';
import { toast } from 'react-toastify';
import useBarrioLayers from '../hooks/useBarrioLayers';
import usePointInTarget from '../hooks/usePointInTarget';
import PanelCapas from './map/PanelCapas';
import PanelLeyenda from './map/PanelLeyenda';
import PanelDebug from './map/PanelDebug';
import { useFormContext } from '../context/FormContext';

const MapLocationPicker = ({ onConfirm, onPendingChange, renabapIds = '', rppvapCodigos = '', onOutsideTargetConfirm, onSetOtroTipo, onSetOtroNombre, beneficiaryFeatures = [] }) => {
  useGeographic();
  const { formA } = useFormContext();
  const partido = formA?.datosGenerales?.partido;

  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const markerSourceRef = useRef(new VectorSource());
  const beneficiaryVectorSource = useRef(new VectorSource());
  const wktFormatRef = useRef(new WKT());
  const [selectedLonLat, setSelectedLonLat] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const arbaLayerRef = useRef(null);
    const beneficiaryLayerRef = useRef(null);
    const tooltipOverlayRef = useRef(null);
    const barrioHoverSource = useRef(new VectorSource());

  const [showNonTargetOverlay, setShowNonTargetOverlay] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const isFromSearchRef = useRef(false);
  const showNonTargetOverlayRef = useRef(false);

  // Avisar al modal padre si hay un punto colocado pero aún sin confirmar
  useEffect(() => {
    onPendingChange?.(!!selectedLonLat);
  }, [selectedLonLat, onPendingChange]);

  const handleShowNonTargetOverlay = (checked) => {
    showNonTargetOverlayRef.current = checked;
    setShowNonTargetOverlay(checked);
  };

  const { targetFeaturesRef, allBarriosFeaturesRef, barrioCounts, layerVisibility, toggleLayer: toggleBarrioLayer, hasRenabapTargets, hasRppvapTargets, hasRenabapData, hasRppvapData, layersRef: barrioLayersRef } = useBarrioLayers({
    map, partido, renabapIds, rppvapCodigos,
  });

  const [beneficiariosVisible, setBeneficiariosVisible] = useState(true);
  const toggleLayer = (key) => {
    if (key === 'beneficiarios') {
      setBeneficiariosVisible(prev => {
        const next = !prev;
        beneficiaryLayerRef.current?.setVisible(next);
        return next;
      });
    } else {
      toggleBarrioLayer(key);
    }
  };

  const { confirmIfOutside, findAllBarrios } = usePointInTarget(targetFeaturesRef, allBarriosFeaturesRef, onOutsideTargetConfirm);

  const handleSearchSubmit = async () => {
    if (!searchQuery.trim() || searchLoading || !map) return;
    setSearchLoading(true);
    isFromSearchRef.current = true;
    try {
      const partidoNombre = formA?.datosGenerales?.partido_nombre;
      const coords = await geocodeAddress(searchQuery, '', '', partidoNombre);
      if (coords) {
        const lonLat = [coords.lng, coords.lat];
        markerSourceRef.current.clear();
        const feature = new Feature({ geometry: new Point(lonLat) });
      markerSourceRef.current.addFeature(feature);
      setSelectedLonLat(lonLat);
      isFromSearchRef.current = false;
        map.getView().animate({ center: lonLat, zoom: 17, duration: 1000 });
      } else {
        isFromSearchRef.current = false;
        toast.warning('No se encontró la dirección con ese término.');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const checkGeom = (geom, c) => {
    if (typeof geom.containsXY === 'function') return geom.containsXY(c[0], c[1]);
    if (geom.getPolygons) return geom.getPolygons().some(p => checkGeom(p, c));
    return false;
  };

  const { result: geoResult, loading: geoLoading, error: geoError } = useNominatim(
    selectedLonLat?.[1],
    selectedLonLat?.[0]
  );

  // Cuando se revierte geocodifica por clic en mapa, llenar el input con la dirección
  useEffect(() => {
    if (geoResult?.direccion && selectedLonLat && !isFromSearchRef.current) {
      setSearchQuery(geoResult.direccion);
    }
  }, [geoResult?.direccion, selectedLonLat]);

  useEffect(() => {
    const markerLayer = new VectorLayer({
      source: markerSourceRef.current,
      style: new Style({
        image: new CircleStyle({
          radius: 10,
          fill: new Fill({ color: '#e74c3c' }),
          stroke: new Stroke({ color: '#fff', width: 3 }),
        }),
      }),
    });

    const arbaWmsLayer = new TileLayer({
      source: new TileWMS({
        url: 'https://geo.arba.gov.ar/geoserver/idera/wms',
        params: {
          'LAYERS': 'idera:Parcela',
          'TILED': true,
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1',
          'SRS': 'EPSG:3857'
        },
        attributions: '© ARBA',
        crossOrigin: 'anonymous'
      }),
      visible: false,
      opacity: 0.8,
    });
    arbaLayerRef.current = arbaWmsLayer;

    const instance = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        arbaWmsLayer,
        markerLayer,
      ],
      view: new View({
        center: [-60.50, -36.67],
        zoom: 5,
      }),
      controls: defaultControls({ attribution: false, zoom: true }),
    });

    instance.getTargetElement().style.cursor = 'crosshair';

    const benLayer = new VectorLayer({
      source: beneficiaryVectorSource.current,
      style: (feature) => {
        const label = feature.get('label');
        return new Style({
          image: new CircleStyle({
            radius: 8,
            fill: new Fill({ color: '#2196F3' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
          }),
          text: label ? new Text({
            text: label,
            fill: new Fill({ color: '#333' }),
            stroke: new Stroke({ color: '#fff', width: 3 }),
            font: '12px sans-serif',
            offsetY: -16,
          }) : undefined,
        });
      },
    });
    instance.addLayer(benLayer);
    beneficiaryLayerRef.current = benLayer;

    const barrioHoverLayer = new VectorLayer({
      source: barrioHoverSource.current,
      style: (feature) => {
        const tipo = feature.get('tipo');
        const color = tipo === 'RENABAP' ? '#F9A825' : tipo === 'RPPVAP' ? '#E53935' : '#666';
        return new Style({
          stroke: new Stroke({ color, width: 4 }),
          fill: new Fill({ color: 'transparent' }),
        });
      },
      zIndex: 10,
    });
    instance.addLayer(barrioHoverLayer);

    const tooltipEl = document.createElement('div');
    tooltipEl.className = 'pointer-events-none bg-black bg-opacity-75 text-white text-xs rounded px-2 py-1 whitespace-nowrap';
    const tooltipOverlay = new Overlay({
      element: tooltipEl,
      offset: [0, -12],
      positioning: 'bottom-center',
    });
    instance.addOverlay(tooltipOverlay);
    tooltipOverlayRef.current = tooltipOverlay;

    setMap(instance);

    return () => {
      instance.setTarget(undefined);
    };
  }, []);

  useEffect(() => {
    if (!map) return;

    const handleClick = (evt) => {
      const lonLat = evt.coordinate;

      markerSourceRef.current.clear();
      const feature = new Feature({ geometry: new Point(lonLat) });
      markerSourceRef.current.addFeature(feature);

      setSelectedLonLat(lonLat);

      // DEBUG: verify point vs barrios
      let dbg = `Targets: ${barrioCounts.targets} | Total: ${barrioCounts.total}`;
      dbg += ` | RENABAP: ${barrioCounts.renabap ?? '?'} | RPPVAP: ${barrioCounts.rppvap ?? '?'}`;
      const foundTargets = [];
      const foundNonTargets = [];
      const seenTarget = new Set();
      for (const tf of targetFeaturesRef.current) {
        const geom = tf.getGeometry();
        if (geom && checkGeom(geom, lonLat)) {
          const key = `${tf.get('tipo')}_${tf.get('id')}`;
          if (seenTarget.has(key)) continue;
          seenTarget.add(key);
          foundTargets.push(tf);
        }
      }
      if (foundTargets.length > 0) {
        dbg += foundTargets.map(tf => `\n✅ TARGET: ${tf.get('nombre')} (${tf.get('tipo')})`).join('');
      }
      const seenNon = new Set();
      for (const bf of allBarriosFeaturesRef.current) {
        const geom = bf.getGeometry();
        if (geom && checkGeom(geom, lonLat)) {
          const key = `${bf.get('tipo')}_${bf.get('id')}`;
          if (seenNon.has(key)) continue;
          seenNon.add(key);
          if (!foundTargets.some(t => t.get('id') === bf.get('id') && t.get('tipo') === bf.get('tipo'))) {
            foundNonTargets.push(bf);
          }
        }
      }
      if (foundNonTargets.length > 0) {
        dbg += foundNonTargets.map(bf => `\n⚠️ NO-TARGET: ${bf.get('nombre')} (${bf.get('tipo')})`).join('');
      }
      if (foundTargets.length === 0 && foundNonTargets.length === 0) {
        dbg += `\n❌ FUERA DE TODO BARRIO`;
      }
      dbg += `\nCoord: ${lonLat[0].toFixed(6)}, ${lonLat[1].toFixed(6)}`;
      setDebugInfo(dbg);
    };

    map.on('click', handleClick);
    const pointerMoveHandler = (evt) => {
      const targetLayers = new Set();
      const bl = barrioLayersRef.current;
      if (bl.renabapTarget) targetLayers.add(bl.renabapTarget);
      if (bl.rppvapTarget) targetLayers.add(bl.rppvapTarget);

      const seen = new Set();
      const hits = [];
      map.forEachFeatureAtPixel(evt.pixel, (f, layer) => {
        if (!f.get('tipo')) return;
        if (!targetLayers.has(layer) && !showNonTargetOverlayRef.current) return;
        const key = `${f.get('tipo')}_${f.get('id')}`;
        if (seen.has(key)) return;
        seen.add(key);
        hits.push(f);
      });
      barrioHoverSource.current.clear();
      const overlay = tooltipOverlayRef.current;
      if (!overlay) return;
      if (hits.length > 0) {
        const sorted = [...hits].sort((a, b) => {
          const ta = a.get('tipo'), tb = b.get('tipo');
          if (ta === 'RENABAP' && tb !== 'RENABAP') return -1;
          if (ta !== 'RENABAP' && tb === 'RENABAP') return 1;
          return 0;
        });
        const lines = sorted.map(h => `${h.get('nombre')} (${h.get('id')}) - ${h.get('tipo')}`);
        overlay.getElement().innerHTML = lines.join('<br>');
        overlay.setPosition(evt.coordinate);
        sorted.forEach(h => barrioHoverSource.current.addFeature(h.clone()));
        map.getTargetElement().style.cursor = 'pointer';
      } else {
        overlay.setPosition(undefined);
        map.getTargetElement().style.cursor = 'crosshair';
      }
    };
    map.on('pointermove', pointerMoveHandler);

    return () => {
      map.un('click', handleClick);
      map.un('pointermove', pointerMoveHandler);
    };
  }, [map, barrioCounts, targetFeaturesRef, allBarriosFeaturesRef, renabapIds, rppvapCodigos]);

  useEffect(() => {
    if (!map) return;
    beneficiaryVectorSource.current.clear();
    if (beneficiaryFeatures.length > 0) {
      const features = beneficiaryFeatures
        .filter(b => b.wkt)
        .map(b => {
          try {
            const feature = wktFormatRef.current.readFeature(b.wkt, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:4326',
            });
            feature.set('label', b.label);
            feature.setId(b.id);
            return feature;
          } catch (e) {
            console.warn('Error parsing beneficiary WKT:', e);
            return null;
          }
        })
        .filter(Boolean);
      features.forEach(f => beneficiaryVectorSource.current.addFeature(f));
    }
  }, [map, beneficiaryFeatures]);

  const handleConfirm = useCallback(async () => {
    if (!selectedLonLat || isConfirming) return;

    let foundBarrio = null;

    if (geoResult?.barrio) {
      const found = findAllBarrios(selectedLonLat);
      if (found.length === 0) {
        const confirmed = window.confirm(
          `El punto no está dentro de ninguno de los barrios seleccionados.\nSe agregará "${geoResult.barrio}" como barrio "Otro".\n¿Desea confirmar la ubicación?`
        );
        if (!confirmed) return;
        if (onSetOtroTipo) onSetOtroTipo();
        if (onSetOtroNombre) {
          onSetOtroNombre(geoResult.barrio);
          alert(`Se agregó "${geoResult.barrio}" como barrio "Otro".`);
        }
        foundBarrio = null;
      } else {
        const result = confirmIfOutside(selectedLonLat);
        if (!result.confirmed) return;
        foundBarrio = result.barrio;
      }
    } else {
      const result = confirmIfOutside(selectedLonLat);
      if (!result.confirmed) return;
      foundBarrio = result.barrio;

      if (!foundBarrio) {
        if (onSetOtroTipo) onSetOtroTipo();
        if (onSetOtroNombre) alert('No se pudo obtener el nombre del barrio desde OpenStreetMap.');
      }
    }

    setIsConfirming(true);

    const features = markerSourceRef.current.getFeatures();
    if (features.length === 0) return;

    const wktFormat = new WKT();
    const wkt = wktFormat.writeFeature(features[0]);

    onConfirm({
      lat: selectedLonLat[1],
      lng: selectedLonLat[0],
      wkt,
      direccion: geoResult?.direccion || '',
      barrio: geoResult?.barrio || '',
      localidad: geoResult?.localidad || '',
      displayName: geoResult?.displayName || '',
      barrioDetectado: foundBarrio,
    });
  }, [selectedLonLat, isConfirming, geoResult, onConfirm, confirmIfOutside, findAllBarrios, onSetOtroTipo, onSetOtroNombre]);

  return (
    <div className="h-full flex flex-col">
      <div ref={mapRef} className="flex-grow relative">

        <PanelDebug barrioCounts={barrioCounts} debugInfo={debugInfo} />

        <div className="absolute top-2 left-2 flex flex-col gap-2 z-10 max-w-[220px]">
          <PanelCapas layerVisibility={{ ...layerVisibility, beneficiarios: beneficiariosVisible }} toggleLayer={toggleLayer} hasRenabapData={hasRenabapData} hasRppvapData={hasRppvapData} showBeneficiarios={beneficiaryFeatures.length > 0} hasRenabapTargets={hasRenabapTargets.current} hasRppvapTargets={hasRppvapTargets.current} />

          <label className="flex items-center gap-2 bg-white bg-opacity-95 rounded-lg shadow-lg px-3 py-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showNonTargetOverlay}
              onChange={(e) => handleShowNonTargetOverlay(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-xs text-gray-700">Mostrar info</span>
          </label>
        </div>

        <PanelLeyenda hasRenabapData={hasRenabapData} hasRppvapData={hasRppvapData} showAsignandoPunto={true} hasRenabapTargets={hasRenabapTargets.current} hasRppvapTargets={hasRppvapTargets.current} />

        {/* Buscador de direcciones + confirmación — borde derecho, como en Editar Geo */}
        <div className="absolute top-3 right-3 z-30 w-[36rem] max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
            Ingresá una dirección para ubicar al beneficiario o hacé clic en el mapa:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              placeholder="Ej: Calle, Localidad"
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearchSubmit}
              disabled={searchLoading || !searchQuery.trim()}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded font-medium transition-colors"
            >
              {searchLoading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>

          {/* Información de dirección y botones de confirmación */}
          {selectedLonLat && (
            <div className="mt-3 space-y-2">
              {geoLoading ? (
                <p className="text-xs text-gray-400 italic">Obteniendo dirección...</p>
              ) : geoError ? (
                <p className="text-xs text-red-500">Error al obtener dirección</p>
              ) : geoResult?.direccion ? (
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{geoResult.direccion}</p>
                  {geoResult.barrio && <p>Barrio: {geoResult.barrio}</p>}
                  {geoResult.localidad && <p>Localidad: {geoResult.localidad}</p>}
                </div>
              ) : null}
              <div className="flex gap-2">
                <button
                  onClick={() => { markerSourceRef.current.clear(); setSelectedLonLat(null); setSearchQuery(''); isFromSearchRef.current = false; }}
                  className="flex-1 px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={geoLoading || isConfirming}
                  className={`flex-1 px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    geoLoading || isConfirming
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isConfirming ? 'Confirmando...' : 'Confirmar ubicación'}
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default MapLocationPicker;
